import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroSearch from '@/components/marketplace/HeroSearch';
import ListingCard from '@/components/marketplace/ListingCard';
import SEOHead from '@/components/marketplace/SEOHead';
import { SponsoredAdsRow } from '@/components/marketplace/SponsoredAd';
import SmartSlot from '@/components/marketplace/SmartSlot';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  Cog, Wrench, Layers, Zap, Truck, Package, ShieldCheck, ClockAlert, Search, MessageSquare, Handshake, Factory, ArrowRight, PlusCircle, LayoutDashboard,
} from 'lucide-react';

const catIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('machin')) return Cog;
  if (n.includes('tool') || n.includes('spare')) return Wrench;
  if (n.includes('raw')) return Layers;
  if (n.includes('electric') || n.includes('electron')) return Zap;
  if (n.includes('material handling') || n.includes('utility') || n.includes('heavy')) return Truck;
  return Package;
};

const Home = () => {
  const { user } = useAuth();
  const [sellLatest, setSellLatest] = useState([]);
  const [wantedLatest, setWantedLatest] = useState([]);
  const [rentLatest, setRentLatest] = useState([]);
  const [serviceLatest, setServiceLatest] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationPages, setLocationPages] = useState({}); // city -> slug

  useEffect(() => {
    Promise.all([
      api.get('/listings/public', { params: { listing_type: 'sell', limit: 6 } }),
      api.get('/listings/public', { params: { listing_type: 'wanted', limit: 6 } }),
      api.get('/listings/public', { params: { listing_type: 'rent', limit: 6 } }),
      api.get('/listings/public', { params: { listing_type: 'service', limit: 6 } }),
      api.get('/categories', { params: { listing_type: 'sell', parent_id: 'null' } }),
      api.get('/locations/popular', { params: { limit: 12 } }),
      api.get('/location-pages'),
    ]).then(([s, w, r, sv, c, l, lp]) => {
      setSellLatest(s.data.items);
      setWantedLatest(w.data.items);
      setRentLatest(r.data.items);
      setServiceLatest(sv.data.items);
      setCategories(c.data);
      setLocations(l.data);
      const map = {};
      (lp.data || []).forEach((p) => { map[p.city] = p.slug; });
      setLocationPages(map);
    }).catch(() => {});
  }, []);

  return (
    <>
      <SEOHead
        title='IndusFact B2B Solutions — Industrial Machinery, Spares, Materials & Equipment Marketplace'
        description="India's B2B industrial classifieds. Buy, sell, and rent CNC machines, forklifts, spares, raw materials, and industrial equipment. Verified sellers, moderated listings, no middlemen."
        keywords='industrial marketplace India, B2B classifieds, used machinery, industrial rental, forklift, CNC, spares'
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/` : undefined}
        schemaEndpoint='/seo/home'
      />
      {/* Hero */}
      <section className='relative indus-hero-bg indus-noise'>
        <div className='indus-container py-14 lg:py-20'>
          <div className='grid lg:grid-cols-12 gap-8 items-center'>
            <div className='lg:col-span-7 space-y-5'>
              <div className='inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1 text-xs font-medium text-muted-foreground'>
                <span className='h-1.5 w-1.5 rounded-full bg-accent' />
                India’s trusted B2B industrial classifieds
              </div>
              <h1 className='text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]'>
                Buy, sell, rent or hire
                <br />
                <span className='text-primary'>industrial machinery</span>, spares, materials &amp; services
              </h1>
              <p className='text-base sm:text-lg text-muted-foreground max-w-2xl'>
                Discover verified industrial listings and service providers from across India. Post your own listing, receive direct inquiries, and reveal contact details after login. Company identity stays private until the buyer engages.
              </p>
              <div className='flex flex-wrap gap-3'>
                {user ? (
                  <>
                    <Link to='/post'>
                      <Button size='lg' data-testid='hero-post-cta'>
                        <PlusCircle className='h-4 w-4 mr-1.5' /> Post a listing
                      </Button>
                    </Link>
                    <Link to={user.role === 'admin' ? '/admin' : user.role === 'moderator' ? '/moderator' : '/dashboard'}>
                      <Button size='lg' variant='outline' data-testid='hero-dashboard-cta'>
                        <LayoutDashboard className='h-4 w-4 mr-1.5' /> Go to Dashboard
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to='/register'>
                      <Button size='lg' data-testid='hero-register-cta'>
                        Get started free
                      </Button>
                    </Link>
                    <Link to='/sell'>
                      <Button size='lg' variant='outline' data-testid='hero-browse-cta'>
                        Browse listings <ArrowRight className='h-4 w-4 ml-1.5' />
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className='lg:col-span-5'>
              <HeroSearch />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className='indus-container -mt-4 relative z-10'>
        <Card className='p-4 sm:p-6 grid grid-cols-2 sm:grid-cols-4 gap-4' data-testid='home-trust-strip'>
          {[
            { icon: ShieldCheck, k: 'Verified sellers', v: 'GST + Company checked' },
            { icon: ClockAlert, k: 'Listings reviewed', v: 'Within 24 hours' },
            { icon: Factory, k: 'B2B focused', v: 'Industrial only · No consumers' },
            { icon: Handshake, k: 'Direct inquiry', v: 'No middlemen, no commissions' },
          ].map(({ icon: Icon, k, v }) => (
            <div key={k} className='flex items-start gap-3'>
              <div className='h-9 w-9 rounded-lg bg-secondary flex items-center justify-center'>
                <Icon className='h-4 w-4 text-primary' />
              </div>
              <div>
                <div className='font-semibold text-sm'>{k}</div>
                <div className='text-xs text-muted-foreground'>{v}</div>
              </div>
            </div>
          ))}
        </Card>
      </section>

      {/* Sponsored — home hero banner placement (renders only if an ad exists) */}
      <section className='indus-container pt-6' data-testid='home-hero-sponsored'>
        <SponsoredAdsRow placement='home_hero' limit={1} />
      </section>

      {/* Category tiles */}
      <section className='indus-container py-14'>
        <div className='flex items-end justify-between mb-6'>
          <div>
            <h2 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Browse by category</h2>
            <p className='text-muted-foreground text-sm'>Industrial machinery, tools, spares, materials, and equipment</p>
          </div>
          <Link to='/categories' className='text-sm text-primary hover:underline hidden sm:inline' data-testid='home-see-all-categories'>See all →</Link>
        </div>
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
          {categories.map(c => {
            const Icon = catIcon(c.name);
            return (
              <Link
                to={c.url_path || `/category/sell/${c.path_slug || c.slug}`}
                key={c.id}
                data-testid='category-tile'
                className='group rounded-xl border bg-card p-4 hover:bg-secondary transition-colors'
              >
                <div className='h-10 w-10 rounded-lg bg-secondary flex items-center justify-center mb-3 group-hover:bg-background'>
                  <Icon className='h-5 w-5 text-primary' />
                </div>
                <div className='font-semibold text-sm leading-snug'>{c.name}</div>
                <div className='text-xs text-muted-foreground mt-1'>Sell · Rent · Wanted · Services</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Locations */}
      <section className='indus-container py-6'>
        <div className='flex items-end justify-between mb-4'>
          <div>
            <h2 className='text-xl sm:text-2xl font-semibold tracking-tight'>Top locations</h2>
            <p className='text-muted-foreground text-sm'>Local-first search: locality → city → state → India</p>
          </div>
        </div>
        <div className='flex flex-wrap gap-2' data-testid='home-locations'>
          {locations.length === 0 ? (
            <div className='text-sm text-muted-foreground'>No cities with active listings yet — post the first one!</div>
          ) : locations.map(l => (
            <Link
              key={l.id || `${l.state}-${l.city}`}
              to={locationPages[l.city] ? `/locations/${locationPages[l.city]}` : `/sell?state=${encodeURIComponent(l.state)}&city=${encodeURIComponent(l.city)}`}
              data-testid='location-pill'
              className='rounded-full border bg-background px-3 py-1 text-sm hover:bg-secondary inline-flex items-center gap-1.5'
            >
              <span>{l.city}, {l.state}</span>
              {typeof l.listing_count === 'number' && (
                <span className='text-[10px] text-muted-foreground'>· {l.listing_count}</span>
              )}
            </Link>
          ))}
        </div>
      </section>

      {/* Sponsored ad row */}
      <section className='indus-container py-4' data-testid='home-sponsored-ads'>
        <SponsoredAdsRow placement='home_between' limit={2} />
      </section>

      {/* Latest listings */}
      <section className='indus-container py-14'>
        <div className='flex items-end justify-between mb-6'>
          <div>
            <h2 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Latest listings</h2>
            <p className='text-muted-foreground text-sm'>Fresh from suppliers and buyers across India</p>
          </div>
        </div>
        <Tabs defaultValue='sell'>
          <TabsList data-testid='home-latest-tabs'>
            <TabsTrigger value='sell'>Sell ({sellLatest.length})</TabsTrigger>
            <TabsTrigger value='wanted'>Wanted ({wantedLatest.length})</TabsTrigger>
            <TabsTrigger value='rent'>Rent ({rentLatest.length})</TabsTrigger>
            <TabsTrigger value='service'>Services ({serviceLatest.length})</TabsTrigger>
          </TabsList>
          {[
            { value: 'sell', items: sellLatest, path: '/sell' },
            { value: 'wanted', items: wantedLatest, path: '/wanted' },
            { value: 'rent', items: rentLatest, path: '/rent' },
            { value: 'service', items: serviceLatest, path: '/services' },
          ].map(({ value, items, path }) => (
            <TabsContent key={value} value={value} className='mt-4'>
              {items.length === 0 ? (
                <Card className='p-10 text-center'>
                  <div className='text-muted-foreground'>No {value} listings yet. Be the first to post.</div>
                  <Link to='/post' className='inline-block mt-4'>
                    <Button data-testid={`empty-${value}-post-cta`}>Post {value} listing</Button>
                  </Link>
                </Card>
              ) : (
                <>
                  <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
                    {items.flatMap((l, i) => {
                      const nodes = [<ListingCard key={l.id} listing={l} />];
                      // Insert a sponsored slot at position #4 (after 3rd card)
                      if (i === 2) {
                        nodes.push(
                          <SmartSlot
                            key={`home-slot-${value}`}
                            placement='listing_grid'
                            listingType={value}
                            excludeIds={items.map(x => x.id)}
                          />
                        );
                      }
                      return nodes;
                    })}
                  </div>
                  <div className='mt-6 text-center'>
                    <Link to={path}>
                      <Button variant='outline' data-testid={`view-all-${value}`}>
                        View all {value} listings <ArrowRight className='h-4 w-4 ml-1.5' />
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* How it works */}
      <section className='indus-container py-14'>
        <div className='rounded-2xl border bg-card p-8 lg:p-12'>
          <div className='max-w-2xl'>
            <h2 className='text-2xl sm:text-3xl font-semibold tracking-tight'>How IndusFact works</h2>
            <p className='text-muted-foreground mt-2'>A privacy-first classifieds workflow for industrial buyers and sellers.</p>
          </div>
          <div className='mt-8 grid md:grid-cols-3 gap-5'>
            {[
              { i: 1, icon: Search, t: 'Browse or Post', d: 'Search verified industrial listings, or post your Sell / Wanted / Rent / Service listing in minutes.' },
              { i: 2, icon: MessageSquare, t: 'Send Inquiry', d: 'Contact details stay masked until buyers log in. Send inquiries directly to sellers with your requirements.' },
              { i: 3, icon: Handshake, t: 'Connect Directly', d: 'Reveal contact after login. All reveals are logged for safety. Deal directly, no middleman fees.' },
            ].map(step => (
              <div key={step.i} className='rounded-xl border p-5 bg-background'>
                <div className='flex items-center gap-3'>
                  <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold'>{step.i}</span>
                  <step.icon className='h-4 w-4 text-primary' />
                </div>
                <div className='mt-3 font-semibold'>{step.t}</div>
                <div className='text-sm text-muted-foreground mt-1'>{step.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className='indus-container py-14'>
        <div className='rounded-2xl border bg-primary text-primary-foreground p-8 lg:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6'>
          <div>
            <h3 className='text-2xl font-semibold tracking-tight'>
              {user ? 'Ready to post your next listing?' : 'Ready to reach industrial buyers across India?'}
            </h3>
            <p className='text-primary-foreground/80 mt-1'>
              {user ? 'Post another Sell, Wanted, Rent or Service listing in minutes.' : 'Register free, post your first listing, and start receiving inquiries.'}
            </p>
          </div>
          <div className='flex gap-3'>
            {user ? (
              <>
                <Link to='/post'>
                  <Button size='lg' className='bg-accent text-accent-foreground hover:bg-accent/90' data-testid='footer-cta-post'>
                    <PlusCircle className='h-4 w-4 mr-1.5' /> Post a listing
                  </Button>
                </Link>
                <Link to='/sell'>
                  <Button size='lg' variant='outline' className='bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10'>
                    Explore listings
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to='/register'>
                  <Button size='lg' className='bg-accent text-accent-foreground hover:bg-accent/90' data-testid='footer-cta-register'>
                    Create free account
                  </Button>
                </Link>
                <Link to='/sell'>
                  <Button size='lg' variant='outline' className='bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10'>
                    Explore listings
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
